import React from 'react';
import { MapPin } from 'lucide-react';
import { getCategoryByTag, categoryStyles, identityData } from '../../constants/identities';

export interface TagItem {
  id: string;
  label: string;
  /** True if this tag overlaps with the viewing user's identities. */
  isMatching: boolean;
}

interface TagboardProps {
  displayName: string;
  /** @username — the @ prefix is stripped internally if present. */
  username: string;
  /** Where the user currently lives — small text below display name. */
  residence?: string;
  /** Rendered on the right side of the name row — pass the Connect button here. */
  headerRight?: React.ReactNode;
  /** Pre-sorted by affinity descending. The component does not reorder tags. */
  tags: TagItem[];
  /** Maximum tags to display. Default 9. */
  maxTags?: number;
  className?: string;
}

/**
 * Tagboard — displays a user's identity tags in a 3-column grid.
 *
 * Has a fixed min-width (220px) so the grid always has a stable reference for
 * the 3 equal columns. Pills fill their cell width; very long labels get ellipsis.
 * headerRight slot: used for the Connect button so it sits next to the name.
 */
export function Tagboard({
  displayName,
  username,
  residence,
  headerRight,
  tags,
  maxTags = 9,
  className = '',
}: TagboardProps) {
  const visibleTags = tags.slice(0, maxTags);

  return (
    <div
      className={`rounded-2xl p-4 ${className}`}
      style={{
        background: 'rgba(0, 0, 0, 0.72)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        minWidth: '220px',
      }}
    >
      {/* Header: display name + optional right slot (Connect button) */}
      <div className="flex items-start justify-between gap-2">
        <p
          className="font-american-captain text-white uppercase leading-tight min-w-0"
          style={{ fontSize: '1.2rem', letterSpacing: '0.05em' }}
        >
          {displayName}
        </p>
        {headerRight && (
          <div className="shrink-0 mt-0.5">{headerRight}</div>
        )}
      </div>

      {/* @username */}
      <p
        className="font-american-captain uppercase"
        style={{
          fontSize: '0.70rem',
          letterSpacing: '0.1em',
          color: 'rgba(255,255,255,0.50)',
          marginTop: '2px',
        }}
      >
        @{username.replace(/^@/, '')}
      </p>

      {/* Residence — below username */}
      {residence && (
        <div className="flex items-center gap-0.5 mt-1">
          <MapPin className="w-2.5 h-2.5 shrink-0" style={{ color: 'rgba(255,255,255,0.45)' }} />
          <span
            style={{
              fontSize: '0.62rem',
              color: 'rgba(255,255,255,0.50)',
              letterSpacing: '0.03em',
              whiteSpace: 'nowrap',
            }}
          >
            {residence}
          </span>
        </div>
      )}

      {/* Divider */}
      <div
        className="w-full mt-2 mb-3"
        style={{ height: '1px', background: 'rgba(255,255,255,0.18)' }}
      />

      {/* Tag grid — 3 equal columns, pills fill their cell, long labels get ellipsis */}
      {visibleTags.length === 0 ? (
        <p className="text-xs text-center py-3" style={{ color: 'rgba(255,255,255,0.28)' }}>
          No tags yet
        </p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
          {visibleTags.map(tag => (
            <TagPill key={tag.id} tag={tag} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Private pill renderer ─────────────────────────────────────────────────────

function TagPill({ tag }: { tag: TagItem }) {
  let category = getCategoryByTag(tag.label);
  if (category === 'Interests') {
    const lower = tag.label.toLowerCase();
    outer: for (const [cat, tags] of Object.entries(identityData)) {
      for (const t of tags) {
        if (t.toLowerCase() === lower) {
          category = cat;
          break outer;
        }
      }
    }
  }
  const theme = categoryStyles[category] ?? categoryStyles['Interests'];

  return (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        padding: '4px 4px',
        fontSize: '9px',
        fontWeight: 800,
        color: '#ffffff',
        borderRadius: '1rem',
        backgroundImage: theme.gradient,
        textShadow: '0 1px 2px rgba(0,0,0,0.9)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        boxShadow: tag.isMatching
          ? '0 0 0 1.5px #1e3a8a, 0 2px 8px rgba(0,0,0,0.35)'
          : '0 0 0 1px #1e3a8a, 0 2px 8px rgba(0,0,0,0.35)',
      }}
    >
      {tag.label}
    </span>
  );
}
